// IMPORTANT: This formula MUST match the backend computeTotals exactly.
// Backend source: refonte-panier-bio-paris/src/shared/core/domain/totals/compute-totals.ts
// If you modify this file, you MUST also update the backend and run both test suites.
// Convention: item.price is stored as TTC. HT and TVA are derived via item.tvaRate.

import { Totals } from '../../shared/types/totals.type';

export type ItemForTotals = {
  price: number;
  quantity: number;
  tvaRate: string;
};

export type GlobalDiscount = {
  value: number;
  reduction: 'percent' | 'flat';
};

export type ComputeTotalsLocalInput = {
  items: ItemForTotals[];
  companyDiscountPercent?: number;
  globalDiscount?: GlobalDiscount;
  deliveryCost?: number;
};

type LineTotals = {
  lineTtcNet: number;
  lineHt: number;
  lineTax: number;
  lineDiscount: number;
  taxRate: string;
};

const computeLineTotals = (item: ItemForTotals, companyDiscountPercent: number): LineTotals => {
  const lineTtcRaw = item.price * item.quantity;
  const lineDiscount = lineTtcRaw * (companyDiscountPercent / 100);
  const lineTtcNet = lineTtcRaw - lineDiscount;
  const taxRateNumber = Number(item.tvaRate.replace(',', '.'));
  const lineTax = (lineTtcNet * taxRateNumber) / (100 + taxRateNumber);
  const lineHt = lineTtcNet - lineTax;

  return {
    lineTtcNet,
    lineHt,
    lineTax,
    lineDiscount,
    taxRate: item.tvaRate,
  };
};

export const computeTotalsLocal = (input: ComputeTotalsLocalInput): Totals => {
  const companyDiscountPercent = input.companyDiscountPercent ?? 0;
  const deliveryCost = input.deliveryCost ?? 0;

  const lineTotalsList = input.items.map((item) => computeLineTotals(item, companyDiscountPercent));

  const itemsTotalTtc = lineTotalsList.reduce((sum, line) => sum + line.lineTtcNet, 0);
  const itemsTotalHt = lineTotalsList.reduce((sum, line) => sum + line.lineHt, 0);
  const itemsTotalDiscount = lineTotalsList.reduce((sum, line) => sum + line.lineDiscount, 0);

  const taxes = lineTotalsList.reduce<Record<string, number>>((acc, line) => {
    acc[line.taxRate] = (acc[line.taxRate] ?? 0) + line.lineTax;
    return acc;
  }, {});

  let globalDiscountAmount = 0;
  if (input.globalDiscount) {
    globalDiscountAmount =
      input.globalDiscount.reduction === 'percent'
        ? itemsTotalTtc * (input.globalDiscount.value / 100)
        : Math.min(input.globalDiscount.value, itemsTotalTtc);
  }

  const globalDiscountHt =
    itemsTotalTtc > 0 ? globalDiscountAmount * (itemsTotalHt / itemsTotalTtc) : 0;

  return {
    totalTtc: itemsTotalTtc - globalDiscountAmount + deliveryCost,
    totalHt: itemsTotalHt - globalDiscountHt + deliveryCost,
    totalDiscount: itemsTotalDiscount + globalDiscountAmount,
    deliveryCost,
    taxes,
  };
};
